import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  collectionData,
  query,
  orderBy,
  writeBatch,
  getDocs,
  QueryDocumentSnapshot,
  startAfter,
  getDoc ,
  increment,
} from '@angular/fire/firestore';
import { serverTimestamp } from '@angular/fire/firestore';
import { map, Observable, from, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { limit } from '@angular/fire/firestore';
import { BehaviorSubject, ReplaySubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AdminCommunicationService {
  constructor(private firestore: Firestore) {}

  private startChatTrigger = new ReplaySubject<any>(1);
  startChatTrigger$ = this.startChatTrigger.asObservable();

  triggerStartChat(user: any) {
    this.startChatTrigger.next(user);
  }

  async syncUserToFirestore(user: any) {
    if (!user?.userId) {
      console.error('❌ userId missing', user);
      return;
    }
    const userId = String(user.userId);
    const ref = doc(this.firestore, 'users', userId);
    await setDoc(
      ref,
      {
        userName: `${user?.firstName} ${user?.lastName} `,
        userAvatar: user?.profileImage || '',
        userEmail: user?.email,
        userPhone: user?.phoneNumber,
        userPhoneCode: user?.phoneCode,
        isVerified: user?.isVerifyEmail,
        role: user?.currentRole,
        createdAt: serverTimestamp(),
      },
      { merge: true } 
    );
  }

  getConversationId(id1: string, id2: string): string {
    const a= String(id1);
    const b= String(id2)
    const ids = [a, b].sort((x, y) =>
    x.localeCompare(y, undefined, { numeric: true })
  );
    return `${ids[0]}_${ids[1]}`;
  }

  async onboardUser(params: {
    userId: string;
    name: string;
    email: string;
    avatar: string;
    phoneNo: string;
    phoneCode: string;
    isVerified: boolean;
    role: string;
  }): Promise<void> {
    const userRef = doc(this.firestore, 'users', String(params.userId));

    await setDoc(
      userRef,
      {
        userName: params.name ?? "",
        userAvatar: params.avatar ?? "",
        userEmail: params.email ?? "",
        userPhone: params.phoneNo ?? "",
        userPhoneCode: params.phoneCode ?? "",
        isVerified: params.isVerified ?? false,
        role: params.role ?? "",
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );
  }

  async getAllUsers(currentUserId: string): Promise<any[]> {
    const ref = collection(this.firestore, 'users');
    const snapshot = await getDocs(ref);
    return snapshot.docs
      .filter((doc) => doc.id != String(currentUserId))
      .map((doc) => {
        const u: any = doc.data();
        return {
          id: doc.id,
          name: u.userName,
          email: u.userEmail,
          profileImage: u.userAvatar || '',
          userPhone: u.userPhone,
          userPhoneCode: u.userPhoneCode,
          role: u.role
        };
      });
  }

  async getUserByIdOnce(userId: string): Promise<any | null> {
  const userRef = doc(this.firestore, 'users', String(userId));
  const snap = await getDoc(userRef);

  if (!snap.exists()) {
    return null;
  }

  return { id: snap.id, ...snap.data() };
}

  getChats(userId: string): Observable<any[]> {
    const ref = collection(this.firestore, `users/${userId}/chat_list`);
    const q = query(ref, orderBy('lastMessageTime', 'desc'));
    return collectionData(q, { idField: 'id' }).pipe(
      switchMap((chats: any[]) => {
        if (!chats.length) {
          return of([]);
        }
        const requests = chats.map(async (chat) => {
          const userSnap = await getDoc(
            doc(this.firestore, 'users', String(chat.receiverId))
          );
          if (userSnap.exists()) {
            const user: any = userSnap.data();
            return {
              ...chat,
              name: user.userName,
              receiverProfileImage: user.userAvatar,
            };
          }
          return chat;
        });
        return from(Promise.all(requests));
      })
    );
  }


  getMessages(chatId: string): Observable<any[]> {
    const ref = collection(this.firestore, `chats/${chatId}/messages`);
    const q = query(ref, orderBy('timestamp', 'asc'));
    return collectionData(q, { idField: 'id' });
  }

  // Load older messages
//   async getMoreMessages(
//     chatId: string,
//     lastDoc: QueryDocumentSnapshot
//   ): Promise<QueryDocumentSnapshot[]> {
//     const ref = collection(this.firestore, `chats/${chatId}/messages`);
//     const q = query(
//       ref,
//       orderBy('timestamp', 'desc'),
//       startAfter(lastDoc),
//       limit(30)
//     );
//     const snap = await getDocs(q);
//     return snap.docs;
//   }

  async sendTextMessage(params: {
    chatId: string;
    senderId: string;
    senderName: string;
    senderProfile: string;
    receiverId: string;
    receiverName: string;
    receiverProfile: string;
    text: string;
  }): Promise<void> {
    const batch = writeBatch(this.firestore);
    const messageRef = doc(
      collection(this.firestore, `chats/${params.chatId}/messages`)
    );

    const senderChatRef = doc(
      this.firestore,
      `users/${params.senderId}/chat_list/${params.chatId}`
    );
    const receiverChatRef = doc(
      this.firestore,
      `users/${params.receiverId}/chat_list/${params.chatId}`
    );

    batch.set(messageRef, {
      senderId: params.senderId,
      receiverId: params.receiverId,
      text: params.text,
      isImage: false,
      isFile: false,
      timestamp: serverTimestamp(),
      status: 'sent',
      deliveredAt: null,
      readAt: null,
    });
    //  Sender chat list update
    batch.set(
      senderChatRef,
      {
        lastMessage: params.text,
        lastMessageTime: serverTimestamp(),
        isUnread: false,
        name: params.receiverName ?? "",
        receiverId: params.receiverId ?? "",
        receiverProfileImage: params.receiverProfile ?? "",
      },
      { merge: true }
    );

    //  Receiver chat list 
    batch.set(
      receiverChatRef,
      {
        lastMessage: params.text,
        lastMessageTime: serverTimestamp(),
        isUnread: true,
        unreadCount: increment(1),
        name: params.senderName ?? "",
        receiverId: params.senderId ?? "",
        receiverProfileImage: params.senderProfile ?? "",
      },
      { merge: true }
    );
    await batch.commit();
  }

  async sendImageMessage(params: {
    chatId: string;
    senderId: string;
    receiverId: string;
    imageUrl: string;
    fileName: string;
    senderName: string;
    senderProfile: string;
    receiverName: string;
    receiverProfile: string;
  }): Promise<void> {
    const batch = writeBatch(this.firestore);

    const messageRef = doc(
      collection(this.firestore, `chats/${params.chatId}/messages`)
    );


    const senderChatRef = doc(
      this.firestore,
      `users/${params.senderId}/chat_list/${params.chatId}`
    );

    const receiverChatRef = doc(
      this.firestore,
      `users/${params.receiverId}/chat_list/${params.chatId}`
    );

    batch.set(messageRef, {
        senderId: params.senderId,
        receiverId: params.receiverId,
        imageUrl: params.imageUrl,
        fileName: params.fileName,
        isImage: true,
        isFile: false,
        timestamp: serverTimestamp(),
        status: 'sent',
        deliveredAt: null,
        readAt: null,
    });

    batch.set(
      senderChatRef,
      {
        lastMessage: '📷 Image',
        isImage: true,
        isFile: false,
        isUnread: false,
        name: params.receiverName ?? "",
        receiverId: params.receiverId ?? "",
        receiverProfileImage: params.receiverProfile ?? "",
        lastMessageTime: serverTimestamp(),
      },
      { merge: true }
    );

    batch.set(
      receiverChatRef,
      {
        lastMessage: '📷 Image',
        isImage: true,
        isFile: false,
        isUnread: true,
        unreadCount: increment(1),
        name: params.senderName ?? "",
        receiverId: params.senderId ?? "",
        receiverProfileImage: params.senderProfile ?? "",
        lastMessageTime: serverTimestamp(),
      },
      { merge: true }
    );
    await batch.commit();
    
  }

  async sendFileMessage(params: {
    chatId: string;
    senderId: string;
    receiverId: string;
    fileUrl: string;
    fileName: string;
    senderName: string;
    senderProfile: string;
    receiverName: string;
    receiverProfile: string;
  }): Promise<void> {
    const batch = writeBatch(this.firestore);

    const messageRef = doc(
      collection(this.firestore, `chats/${params.chatId}/messages`)
    );

    const senderChatRef = doc(
      this.firestore,
      `users/${params.senderId}/chat_list/${params.chatId}`
    );

    const receiverChatRef = doc(
      this.firestore,
      `users/${params.receiverId}/chat_list/${params.chatId}`
    );

    batch.set(messageRef, {
        senderId: params.senderId,
        receiverId: params.receiverId,
        fileUrl: params.fileUrl,
        fileName: params.fileName,
        isImage: false,
        isFile: true,
        timestamp: serverTimestamp(),
        status: 'sent',
        deliveredAt: null,
        readAt: null,
    });

    batch.set(
      senderChatRef,
      {
        lastMessage: '📎 File',
        isImage: false,
        isFile: true,
        isUnread: false,
        name: params.receiverName ?? "",
        receiverId: params.receiverId ?? "",
        receiverProfileImage: params.receiverProfile ?? "",
        lastMessageTime: serverTimestamp(),
      },
      { merge: true }
    );

    batch.set(
      receiverChatRef,
      {
        lastMessage: '📎 File',
        isImage: false,
        isFile: true,
        isUnread: true,
        unreadCount: increment(1),
        name: params.senderName ?? "",
        receiverId: params.senderId ?? "",
        receiverProfileImage: params.senderProfile ?? "",
        lastMessageTime: serverTimestamp(),
      },
      { merge: true }
    );
    await batch.commit();
  }

  async markAsRead(userId: string, chatId: string): Promise<void> {
    await setDoc(
      doc(this.firestore, `users/${userId}/chat_list/${chatId}`),
      {
        isUnread: false,
        unreadCount: 0,
      },
      { merge: true }
    );
  }

  getTotalUnreadCount(userId: string): Observable<number> {
  return this.getChats(userId).pipe(
    map((chats: any[]) => {
      return chats.reduce((total, chat) => {
        return total + (chat.unreadCount || 0);
      }, 0);
    })
  );
}
  async markMessagesAsDelivered(chatId: string, currentUserId: string) {
    const ref = collection(this.firestore, `chats/${chatId}/messages`);
    const snap = await getDocs(ref);
    const batch = writeBatch(this.firestore);
    snap.docs.forEach((docSnap) => {
      const data: any = docSnap.data();

      if (
        data.receiverId == currentUserId &&
        data.status === 'sent'
      ) {
        batch.update(docSnap.ref, {
          status: 'delivered',
          deliveredAt: serverTimestamp(),
        });
      }
    });

    await batch.commit();
  }
  async markMessagesAsRead(chatId: string, currentUserId: string) {
    const ref = collection(this.firestore, `chats/${chatId}/messages`);
    const snap = await getDocs(ref);

    const batch = writeBatch(this.firestore);

    snap.docs.forEach((docSnap) => {
      const data: any = docSnap.data();
      if (
        data.receiverId == currentUserId &&
        data.status != 'read'
      ) {
        batch.update(docSnap.ref, {
          status: 'read',
          readAt: serverTimestamp(),
        });
      }
    });

    await batch.commit();
  }
}
